// Карточка билета («посадочный талон»): маршрут слева, цена и кнопки справа
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import type { Currency, Leg, Ticket, Airport } from '@sayohat/shared';
import { daysBetween, formatDay, formatDuration, money, plural } from '../../../shared/lib/format';
import { createStore, useStore } from '../../../shared/lib/store';
import { Icon, type IconName } from '../../../shared/ui/Icon';
import { extrasOf, isNightArrival, paxTotal, type PaxCount } from '../lib';
import { AirlineLogo } from './AirlineLogo';

export interface TicketLabel {
  cls: string;
  icon: IconName;
  text: string;
}

export function TicketTags({ ticket: t, labels = [] }: { ticket: Ticket; labels?: TicketLabel[] }) {
  return (
    <>
      {labels.map((l) => <span key={l.text} className={`pill ${l.cls}`}><Icon name={l.icon} />{l.text}</span>)}
      {extrasOf(t).live && <span className="pill good live-tag" title="Цена из живого поиска по агентствам"><span className="live-dot" aria-hidden="true" />живая цена</span>}
      {t.to.transfers === 0 && (!t.back || t.back.transfers === 0) && <span className="pill good"><Icon name="zap" />Прямой</span>}
      {t.lowcost && <span className="pill warn" title="У лоукостеров багаж обычно оплачивается отдельно"><Icon name="luggage" />Лоукостер</span>}
      {isNightArrival(t) && <span className="pill" title="Прилёт между 00:00 и 06:00"><Icon name="moon" />Ночной прилёт</span>}
    </>
  );
}

export function PaxNote({ price, pax, currency }: { price: number; pax: PaxCount | null | undefined; currency: Currency }) {
  const p = paxTotal(price, pax);
  if (!p) return null;
  return (
    <div className="price-total" title="Примерно: детские тарифы и тарифы для младенцев зависят от авиакомпании">
      {`≈ ${money(currency).format(p.total)} за ${p.count} ${plural(p.count, 'пассажира', 'пассажиров', 'пассажиров')}`}
    </div>
  );
}

// Открытая подсказка по пересадкам (на телефоне — по нажатию); открыта не больше одной
const stopsStore = createStore({ open: '' });

// Открытая подсказка по пересадкам закрывается нажатием в любом месте.
// Если в этот момент нажали на билет (или «Купить») — только закрываем подсказку, билет не открываем.
let closedByTap = false;
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const focused = document.activeElement instanceof HTMLElement && document.activeElement.closest('[data-action="stops"]') ? document.activeElement : null;
    closedByTap = false;
    if ((!stopsStore.get().open && !focused) || target?.closest('[data-action="stops"]')) return;
    closedByTap = true;
    stopsStore.set({ open: '' });
    focused?.blur();
  }, true);
}

const placeText = (a: Airport) => `${a.city}${a.name && a.name !== a.city ? `, ${a.name}` : ''}`;

function TransfersTip({ leg }: { leg: Leg }) {
  const arr = leg.arrivalAt ? leg.arrivalAt.slice(11, 16) : '—';
  const plusDays = leg.arrivalAt ? daysBetween(leg.departureAt, leg.arrivalAt) : 0;
  const n = leg.transfers || 0;
  const unknown = n - leg.via.length;
  return (
    <span className="tip" role="tooltip">
      <span className="tip-title"><Icon name="plane" />{`${n} ${plural(n, 'пересадка', 'пересадки', 'пересадок')}${leg.duration ? ` · в пути ${formatDuration(leg.duration)}` : ''}`}</span>
      <span className="tip-route">
        <span className="tip-point"><b>{leg.departureAt.slice(11, 16)}</b><span>{`Вылет: ${placeText(leg.from)} `}<i>{leg.from.code}</i></span></span>
        {leg.via.map((v, i) => (
          <span key={`${v.code}-${i}`} className="tip-point via"><b>{i + 1}</b><span>{`Пересадка: ${placeText(v)} `}<i>{v.code}</i></span></span>
        ))}
        {unknown > 0 && <span className="tip-point via"><b>?</b><span>{`${unknown === n ? 'Город пересадки' : 'Ещё одна пересадка'} — уточните у продавца`}</span></span>}
        <span className="tip-point"><b>{arr}{plusDays > 0 && <sup>{`+${plusDays}`}</sup>}</b><span>{`Прилёт: ${placeText(leg.to)} `}<i>{leg.to.code}</i></span></span>
      </span>
      <span className="tip-note"><Icon name="clock" />Время ожидания между рейсами и смену аэропорта покажет продавец</span>
    </span>
  );
}

export function LegView({ leg, stopsKey }: { leg: Leg; stopsKey: string }) {
  const open = useStore(stopsStore, (s) => s.open === stopsKey);
  // Время в API — местное время аэропорта; показываем как есть, без пересчёта в пояс браузера
  const dep = leg.departureAt.slice(11, 16);
  const arr = leg.arrivalAt ? leg.arrivalAt.slice(11, 16) : '—';
  const plusDays = leg.arrivalAt ? daysBetween(leg.departureAt, leg.arrivalAt) : 0;
  const n = leg.transfers;
  const dots = leg.via.length
    ? leg.via.map((_, i) => <span key={i} className="dot" style={{ left: `${((i + 1) / (leg.via.length + 1)) * 100}%` }} />)
    : n && n > 0 ? <span className="dot" style={{ left: '50%' }} /> : null;
  return (
    <div className="leg">
      <div>
        <div className="t">{dep}</div>
        <div className="p">{`${leg.from.city} `}<span title={leg.from.name}>{leg.from.code}</span></div>
        <div className="leg-date">{formatDay(leg.departureAt)}</div>
      </div>
      <div className="track">
        <span className="dur">{leg.duration ? formatDuration(leg.duration) : ''}</span>
        <span className="line">{dots}<Icon name="plane" /></span>
        {n === 0 ? <span className="stops direct">прямой</span>
          : n == null ? null
            : (
              <span className={open ? 'stops-wrap open' : 'stops-wrap'} tabIndex={0} data-action="stops" data-stops={stopsKey}>
                <span className="stops via">{`${n} ${plural(n, 'пересадка', 'пересадки', 'пересадок')}${leg.via.length ? `: ${leg.via.map((a) => a.code).join(', ')}` : ''}`}</span>
                <TransfersTip leg={leg} />
              </span>
            )}
      </div>
      <div className="end">
        <div className="t">{arr}{plusDays > 0 && <sup>{`+${plusDays}`}</sup>}</div>
        <div className="p">{`${leg.to.city} `}<span title={leg.to.name}>{leg.to.code}</span></div>
      </div>
    </div>
  );
}

export interface TicketCardProps {
  ticket: Ticket;
  currency: Currency;
  labels?: TicketLabel[];
  /** сдвиг дат для «соседних дат» */
  shift?: string;
  note?: ReactNode;
  pax?: PaxCount | null;
  fav: boolean;
  compared: boolean;
  onFav: () => void;
  onCompare: () => void;
  onOpen: () => void;
}

export function TicketCard({ ticket: t, currency, labels = [], shift = '', note, pax, fav, compared, onFav, onCompare, onOpen }: TicketCardProps) {
  const fmt = money(currency);
  const price = fmt.format(t.price);

  const onClick = (e: MouseEvent<HTMLElement>) => {
    const target = e.target instanceof Element ? e.target : null;
    if (closedByTap) { closedByTap = false; e.preventDefault(); return; }
    if (!target || target.closest('[data-action="buy"]')) return;
    // На телефоне подсказка по пересадкам открывается нажатием
    const stops = target.closest<HTMLElement>('[data-action="stops"]');
    if (stops) {
      const key = stops.dataset.stops || '';
      stopsStore.set((s) => ({ open: s.open === key ? '' : key }));
      return;
    }
    if (target.closest('[data-action="compare"]')) { onCompare(); return; }
    if (target.closest('[data-action="fav"]')) { onFav(); return; }
    onOpen();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(); };

  return (
    <article className="ticket" data-id={t.id} tabIndex={0} onClick={onClick} onKeyDown={onKeyDown}>
      <div className="ticket-main">
        <div className="ticket-tags">
          {shift && <span className="pill blue"><Icon name="calendar" />{shift}</span>}
          <TicketTags ticket={t} labels={labels} />
        </div>
        <div className="airline"><AirlineLogo code={t.airline} size={28} /><span>{`${t.airlineName} `}<small>{`${t.airline} ${t.flightNumber}`}</small></span></div>
        <LegView leg={t.to} stopsKey={`${t.id}:to`} />
        {t.back && <LegView leg={t.back} stopsKey={`${t.id}:back`} />}
      </div>
      <div className="ticket-stub">
        <span className="stub-tools">
          <button type="button" className={compared ? 'fav cmp on' : 'fav cmp'} data-action="compare" title={compared ? 'Убрать из сравнения' : 'Сравнить'} aria-label="Сравнить"><Icon name="columns" /></button>
          <button type="button" className={fav ? 'fav on' : 'fav'} data-action="fav" title={fav ? 'Убрать из избранного' : 'В избранное'} aria-label="Избранное"><Icon name="star" /></button>
        </span>
        <div>
          <div className={price.length > 11 ? 'price long' : 'price'}>{price}</div>
          {pax && paxTotal(t.price, pax) && <><div className="price-note">за 1 взрослого</div><PaxNote price={t.price} pax={pax} currency={currency} /></>}
          {note && <div className="price-note">{note}</div>}
          {t.seller && <div className="price-note">{`у ${t.seller}`}</div>}
        </div>
        {t.buyUrl && <a className="btn" href={t.buyUrl} target="_blank" rel="noopener sponsored" data-action="buy">Купить</a>}
      </div>
    </article>
  );
}
